import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  output,
  Output,
  signal,
  ViewChild,
} from "@angular/core";

import { slideOverAnimation } from "src/app/shared/animations";
import { ProcessInputOutputTypeEnum } from "src/app/shared/enums";
import {
  IBaseUnit,
  IItem,
  IItemExtended,
  ILocationExtended,
  IMaterial,
  IProcessInput,
  IProcessOutput,
  IProductExtended,
  ISelectOption,
  ProcessInputOutputType,
} from "src/app/shared/interfaces";
import { ProcessesService } from "src/app/shared/services";

import {
  SummaryComponentType,
  SummaryComponentTypeEnum,
} from "@components/processes/edit-process/process-inputs-outputs/process-inputs-outputs.model";
import { ProcessOverlayService } from "@components/processes/pages/process-overlay/process-overlay.service";
import { ProcessInOutTableComponent } from "@components/shared/tables/process-in-out-table/process-in-out-table.component";
import { TextConstants } from "@shared/constants";
import { NotificationService } from "@shared/services";

import { AddProcessInputsOutputsResult } from "../add-process-inputs-outputs/add-process-inputs-outputs.component";

@Component({
  standalone: false,
  selector: "app-process-inputs-outputs",
  templateUrl: "./process-inputs-outputs.component.html",
  styleUrls: ["./process-inputs-outputs.component.scss"],
  animations: [slideOverAnimation],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ProcessInputsOutputsComponent implements OnInit {
  @ViewChild("processInOutTable") processInOutTable: ProcessInOutTableComponent;

  @Input()
  public processId: string;

  @Input()
  public processLocation: ISelectOption;

  @Input()
  public type: ProcessInputOutputType;

  public allUnitOfMeasurements = input<IBaseUnit[]>([]);

  public allProducts = input<IProductExtended[]>([]);

  @Input()
  public allLocations: ILocationExtended[] = [];

  @Input()
  public allMaterials: IMaterial[] = [];

  @Input()
  public displayTitle: boolean = true;

  @Input()
  public summaryComponentType: SummaryComponentType = SummaryComponentTypeEnum.QUANTITY_SUMMARY;

  @Input()
  public isReadOnly: boolean = false;

  @Input()
  public hasEntitiesWriteAccess: boolean = false;

  @Output()
  public changed: EventEmitter<number> = new EventEmitter<number>();

  addCompleted = output<AddProcessInputsOutputsResult>();

  public selectedElements: IProcessInput[] | IProcessOutput[] = [];

  public selectedItems: IItem[] = [];

  public isSlideOverVisible = signal(false);

  public isLoading = signal(true);

  public processInputOrOutput: IProcessInput | IProcessOutput;

  public readonly summaryComponentTypeEnum = SummaryComponentTypeEnum;

  public isInboundShared = input<boolean>(false);

  public readonly processInputOutputTypeEnum = ProcessInputOutputTypeEnum;

  public unitsOfMeasurement = computed(() => {
    if (this.isInboundShared()) {
      return this.allProducts()
        .map((p) => p.unitOfMeasurement)
        .filter((u) => !!u);
    } else {
      return this.allUnitOfMeasurements();
    }
  });

  @Input()
  public inboundSharedSenderOrgId: string = null;

  @Input()
  public inboundProcessResources: IProcessInput[] | IProcessOutput[];

  @Input()
  public inboundItems: IItemExtended[] = [];

  public readonly translations: any = {
    addNewTp: TextConstants.ADD_NEW,
    inputEmptyQuantitySummary: $localize`:@@process-inputs-outputs.inputemptyquantitysummary:Once you add inputs to this process, a summary of their quantities will show up here`,
    outputEmptyQuantitySummary: $localize`:@@process-inputs-outputs.outputemptyquantitysummary:Once you add outputs to this process, a summary of their quantities will show up here`,
    inputSelectedTypeHeader: $localize`:@@process-inputs-outputs.inputselectedheader:Selected inputs`,
    outputSelectedTypeHeader: $localize`:@@process-inputs-outputs.outputselectedheader:Selected outputs`,
    inputs: TextConstants.INPUTS,
    outputs: TextConstants.OUTPUTS,
  };

  private processOverlayService = inject(ProcessOverlayService);

  constructor(
    private processesService: ProcessesService,
    private notificationService: NotificationService,
  ) {}

  public async ngOnInit(): Promise<void> {
    if (this.processId) {
      if (this.isInboundShared()) {
        this.selectedItems = this.inboundItems as any as IItem[];
        this.selectedElements = this.inboundProcessResources;
        this.allMaterials = this.allProducts()
          .map((p) => p.materials)
          .flat();

        this.isLoading.set(false);
      } else {
        // Use data from ProcessOverlayService instead of making new API calls
        await this.onReloadElements();
      }
    }
  }

  public onAdd = (element?: IProcessInput | IProcessOutput): void => {
    this.processInputOrOutput = element;
    this.isSlideOverVisible.set(true);
  };

  public onAddCompleted = async (result: AddProcessInputsOutputsResult): Promise<void> => {
    this.isSlideOverVisible.set(false);

    if (result?.hasSaved) {
      // Refresh the data in the service after addition
      await this.processOverlayService.refreshProcessData(this.processId, this.type);

      // Then reload the component's data
      await this.onReloadElements();
    }

    this.addCompleted.emit(result);
  };

  public onRemove = async (elementId: string): Promise<void> => {
    try {
      if (this.type === ProcessInputOutputTypeEnum.INPUT) {
        await this.processesService.deleteInput(this.processId, elementId);
      } else {
        await this.processesService.deleteOutput(this.processId, elementId);
      }
      this.notificationService.showSuccess(
        this.type === ProcessInputOutputTypeEnum.INPUT
          ? $localize`:@@shared.inputremoved:Input removed`
          : $localize`:@@shared.outputremoved:Output removed`,
      );

      // Refresh the data in the service after deletion
      await this.processOverlayService.refreshProcessData(this.processId, this.type);

      // Then reload the component's data
      await this.onReloadElements();
    } catch (error) {
      this.notificationService.showError(error);
    }
  };

  public onCloseSlideOverlay(): void {
    this.isSlideOverVisible.set(false);
  }

  private onReloadElements = async (): Promise<void> => {
    this.isLoading.set(true);
    this.selectedElements = [];
    this.selectedItems = [];

    try {
      // Check if data is already loaded in the service
      if (this.type === ProcessInputOutputTypeEnum.INPUT) {
        if (this.processOverlayService.processInputs().length === 0) {
          // Load data if not already loaded
          await this.processOverlayService.loadProcessData(this.processId, this.type);
        }
        this.selectedElements = [...this.processOverlayService.processInputs()];
        this.selectedItems = [
          ...(this.processOverlayService.processInputItems() as any as IItem[]),
        ];
      } else {
        if (this.processOverlayService.processOutputs().length === 0) {
          // Load data if not already loaded
          await this.processOverlayService.loadProcessData(this.processId, this.type);
        }
        this.selectedElements = [...this.processOverlayService.processOutputs()];
        this.selectedItems = [
          ...(this.processOverlayService.processOutputItems() as any as IItem[]),
        ];
      }

      this.changed.emit(this.selectedElements.length);
      this.isLoading.set(false);

      // Refresh the ProcessInOutTable component if it exists and we're using it
      if (
        this.processInOutTable &&
        this.summaryComponentType === this.summaryComponentTypeEnum.PROCESS_INPUTS_OUTPUTS
      ) {
        setTimeout(() => {
          this.processInOutTable.refresh();
        });
      }
    } catch (error) {
      this.notificationService.showError(error);
    }
  };

  async getSelectedElements() {
    if (this.isInboundShared()) {
      return this.inboundProcessResources;
    }

    // Use data from ProcessOverlayService instead of making new API calls
    if (this.type === ProcessInputOutputTypeEnum.INPUT) {
      if (this.processOverlayService.processInputs().length === 0) {
        await this.processOverlayService.loadProcessData(this.processId, this.type);
      }

      return [...this.processOverlayService.processInputs()];
    } else {
      if (this.processOverlayService.processOutputs().length === 0) {
        await this.processOverlayService.loadProcessData(this.processId, this.type);
      }

      return [...this.processOverlayService.processOutputs()];
    }
  }
}
